import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestNavigationConfirmation } from '../utils/unsavedChangesRegistry';

/**
 * `navigate()` for a cross-cutting navigator that is NOT an anchor — the module
 * switcher, an overflow menu entry, a redirect after a delete.
 *
 * `useUnsavedChangesWarning` blocks three exits: `beforeunload`, `popstate`, and a
 * capture-phase anchor click. A chrome navigator that renders a `<button>` calling
 * `navigate()` has no anchor to intercept, so the click is invisible to that hook —
 * which is how a sidebar click silently discarded a dirty form on EVERY guarded page
 * in the app, back when every sidebar entry was such a button. (The sidebar's entries
 * are `<AppNavLink>` anchors now, and that component calls this hook too: the two
 * paths converge on one prompt either way.)
 *
 * This closes that hole from the navigator's side: before moving, offer the
 * destination to whichever dirty form is registered. If one takes it, that form's
 * own `ConfirmDialog` opens and its `confirmNavigation` performs the navigation
 * later — so there is exactly one prompt, one copy, and one `navigate()` call,
 * shared with the anchor path. If nothing is dirty, we navigate immediately and
 * the user sees nothing.
 *
 * Returns `true` when the navigation happened, `false` when it was deferred to a
 * prompt — so a caller with side effects of its own can order them accordingly.
 * A navigation to the path already showing never prompts: it does not unmount the
 * form, so there is nothing to lose (the same rule the anchor interceptor applies).
 */
export function useGuardedNavigate(): (destination: string) => boolean {
  const navigate = useNavigate();

  return useCallback(
    (destination: string): boolean => {
      const current = window.location.pathname + window.location.search;
      const next = new URL(destination, window.location.href);
      const isSamePath = next.pathname + next.search === current;

      if (!isSamePath && requestNavigationConfirmation(destination)) {
        return false;
      }
      navigate(destination);
      return true;
    },
    [navigate],
  );
}
